import { Component, OnInit, OnDestroy, ChangeDetectorRef, Input } from '@angular/core';

import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { InteractionsService, AdminInteraction, LogsQueryParams } from '../../../../services/interactions.service';
import { AuthService } from '../../../../services/auth.service';
import { ContractFilter } from '../../../../interfaces/contract-filter.interface';
import { ExtendedLogsTableComponent } from '../extended-logs-table/extended-logs-table.component';
import { SharedFiltersComponent } from '../../../../shared/components/shared-filters/shared-filters.component';

@Component({
  selector: 'app-extended-logs-wrapper',
  standalone: true,
  imports: [
    MatIconModule,
    MatButtonModule,
    MatPaginatorModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSnackBarModule,
    FormsModule,
    ExtendedLogsTableComponent,
    SharedFiltersComponent
],
  templateUrl: './extended-logs-wrapper.component.html',
  styleUrl: './extended-logs-wrapper.component.scss'
})
export class ExtendedLogsWrapperComponent implements OnInit, OnDestroy {
  @Input() initialPageSize: number = 100;
  @Input() customPageSizeOptions: number[] | null = null;
  @Input() title: string = 'Összes rendszerelőzmény';
  
  paginatedLogs: AdminInteraction[] = [];
  loading: boolean = true;
  error: string | null = null;

  // Pagination properties
  pageSize: number = 100;
  pageIndex: number = 0;
  totalLogs: number = 0;
  pageSizeOptions: number[] = [100, 200, 500];
  
  // Status options in the format required by shared-filters component
  customStatusOptions: { value: string; label: string }[] = [];
  
  // User options in the format required by shared-filters component
  customUserOptions: { value: string; label: string }[] = [];
  
  // All active users for filtering
  allUsers: any[] = [];
  
  // Role-based access control
  shouldRestrictToOwnLogs: boolean = false;
  shouldDisableUserFilter: boolean = false;
  shouldExcludeAdminLogs: boolean = false;
  currentUserFullName: string = '';
  
  // Current shared filter state
  currentSharedFilter: ContractFilter = {
    searchTerm: '',
    statusFilter: [],
    biztositoFilter: [],
    assigneeFilter: [],
    dateFromFilter: null,
    dateToFilter: null,
    productFilter: [],
    paymentFrequencyFilter: [],
    currencyFilter: [],
    policyNumberFilter: ''
  };

  // Debounce subject a szerver hívások vezéléséhez
  private filterChange$ = new Subject<void>();
  private filterSubscription: Subscription | null = null;  // Az első filterChange eseményt (a SharedFiltersComponent kezdeti emit-je)
  // figyelmen kívül hagyjuk, mert a wrapper már indít egy betöltést ngOnInit-ben
  private isInitialFilterEmit: boolean = true;
  get hasActiveFilters(): boolean {
    const f = this.currentSharedFilter;
    const statusArr = Array.isArray(f.statusFilter) ? f.statusFilter : [f.statusFilter].filter(Boolean);
    const userArr = Array.isArray(f.biztositoFilter) ? f.biztositoFilter : [f.biztositoFilter].filter(Boolean);
    return !!(f.searchTerm?.trim() || statusArr.length > 0 || userArr.length > 0 || f.dateFromFilter || f.dateToFilter);
  }

  constructor(
    private interactionsService: InteractionsService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit() {    
    this.pageSize = this.initialPageSize;
    if (this.customPageSizeOptions) {
      this.pageSizeOptions = this.customPageSizeOptions;
    }
    
    // Debounce beállítása a szűrő változásokhoz (400ms)
    this.filterSubscription = this.filterChange$
      .pipe(debounceTime(400))
      .subscribe(() => this.loadInteractions());
    
    this.initializeRoleBasedAccess();
    this.loadAllUsers();
    // Első betöltés debounce nélkül (a status options-t a loadInteractions hívja)
    this.loadInteractions();
  }

  ngOnDestroy() {
    this.filterSubscription?.unsubscribe();
  }

  /**
   * Szerver oldali paginált + szűrt interakciók betöltése
   */
  private loadInteractions() {
    this.loading = true;
    this.error = null;
    
    const params = this.buildQueryParams();
    
    // A Típus dropdown opcióit is frissítjük az aktuális (státusz nélküli) szűrők szerint
    this.loadStatusOptions();
    
    this.interactionsService.getAllInteractions(params).subscribe({
      next: (response) => {
        this.paginatedLogs = response.items || [];
        this.totalLogs = response.total;
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        this.snackBar.open(
          `Hiba az adatok betöltése során: ${error.message || error.statusText || 'Ismeretlen hiba'}`,
          'Bezárás',
          {
            duration: 8000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          }
        );
        
        this.error = 'Hiba történt az adatok betöltése során';
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * A szűrő paraméterek összeállítása a szerver híváshoz
   */
  private buildQueryParams(): LogsQueryParams {
    const params: LogsQueryParams = {
      page: this.pageIndex,
      pageSize: this.pageSize
    };
    
    // Szöveges keresés
    const searchTerm = this.currentSharedFilter.searchTerm;
    if (searchTerm && searchTerm.trim()) {
      params.search = searchTerm.trim();
    }
    
    // Státusz szűrő
    const statusFilter = Array.isArray(this.currentSharedFilter.statusFilter)
      ? this.currentSharedFilter.statusFilter
      : [this.currentSharedFilter.statusFilter].filter(Boolean);
    if (statusFilter.length > 0) {
      params.status = statusFilter as string[];
    }
    
    // Felhasználó szűrő
    const userFilter = Array.isArray(this.currentSharedFilter.biztositoFilter)
      ? this.currentSharedFilter.biztositoFilter
      : [this.currentSharedFilter.biztositoFilter].filter(Boolean);
    if (userFilter.length > 0) {
      params.user = userFilter as string[];
    }
    
    // Dátum szűrők (ISO YYYY-MM-DD formátumra konvertálás)
    if (this.currentSharedFilter.dateFromFilter) {
      params.dateFrom = this.formatDateToISO(this.currentSharedFilter.dateFromFilter);
    }
    if (this.currentSharedFilter.dateToFilter) {
      params.dateTo = this.formatDateToISO(this.currentSharedFilter.dateToFilter);
    }
    
    return params;
  }

  private formatDateToISO(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private loadStatusOptions() {
    // A jelenlegi szűrők alapján (státusz nélkül) kérjük le, hogy melyik típusok
    // férnek bele a szűrt halmazba
    const params = this.buildQueryParams();
    const statusOptionsParams = {
      search: params.search,
      user: params.user,
      dateFrom: params.dateFrom,
      dateTo: params.dateTo,
    };
    
    this.interactionsService.getStatusOptions(statusOptionsParams).subscribe({
      next: (options) => {
        this.customStatusOptions = options || [];
        this.cdr.detectChanges();
      },
      error: () => {
        this.customStatusOptions = [];
      }
    });
  }

  private loadAllUsers() {    
    if (this.shouldRestrictToOwnLogs) {
      const currentUser = this.authService.getCurrentUser();
      if (currentUser && this.currentUserFullName) {
        this.allUsers = [{
          name: this.currentUserFullName,
          username: currentUser.username || this.currentUserFullName
        }];
      } else {
        this.allUsers = [];
      }
      this.updateUserOptions();
      this.currentSharedFilter.biztositoFilter = [this.currentUserFullName];
      this.cdr.detectChanges();
      return;
    }
    
    this.authService.getAllUsers().subscribe({
      next: (users: any[]) => {
        // Minden felhasználó látszik a Felhasználó szűrőben (admin, sales_manager, deputy-CEO egyaránt)
        const filteredUsers = users || [];
        
        this.allUsers = filteredUsers;
        this.updateUserOptions();
        
        if (this.shouldRestrictToOwnLogs && this.currentUserFullName) {
          this.currentSharedFilter.biztositoFilter = [this.currentUserFullName];
        }
        
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        this.snackBar.open(
          `Hiba a felhasználók betöltése során: ${error.message || error.statusText || 'Ismeretlen hiba'}`,
          'Bezárás',
          {
            duration: 6000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          }
        );
        
        this.allUsers = [];
        this.updateUserOptions();
      }
    });
  }

  refreshLogs() {
    this.loadInteractions();
  }

  private initializeRoleBasedAccess() {
    this.shouldRestrictToOwnLogs = this.authService.isCallCenterEmployee() || this.authService.isKAM();
    this.shouldDisableUserFilter = this.shouldRestrictToOwnLogs;
    
    const currentUser = this.authService.getCurrentUser();
    this.currentUserFullName = currentUser?.name || '';
    
    // Minden vezető szerepkör (sales_manager, deputy-CEO) láthat minden logot (admin bejegyzéseket is)
    this.shouldExcludeAdminLogs = false;
    
    if (this.shouldRestrictToOwnLogs && this.currentUserFullName) {
      this.currentSharedFilter.biztositoFilter = [this.currentUserFullName];
    }
  }

  onPageChange(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    // Oldalváltás: azonnali szerver hívás, nem debounce-olva
    this.loadInteractions();
  }

  onSharedFilterChange(filter: ContractFilter) {
    // A SharedFiltersComponent kezdeti emit-jét kihagyjuk, hogy ne legyen dupla betöltés
    if (this.isInitialFilterEmit) {
      this.isInitialFilterEmit = false;
      this.currentSharedFilter = { ...filter };
      if (this.shouldRestrictToOwnLogs && this.currentUserFullName) {
        this.currentSharedFilter.biztositoFilter = [this.currentUserFullName];
      }
      return;
    }
    
    this.currentSharedFilter = { ...filter };
    
    if (this.shouldRestrictToOwnLogs && this.currentUserFullName) {
      this.currentSharedFilter.biztositoFilter = [this.currentUserFullName];
    }
    
    // Szűrő változásnál visszaugrunk az első oldalra
    this.pageIndex = 0;
    // Debounce-olt szerver hívás (a szöveges keresés miatt)
    this.filterChange$.next();
  }

  clearFilters() {
    this.currentSharedFilter = {
      searchTerm: '',
      statusFilter: [],
      biztositoFilter: [],
      assigneeFilter: [],
      dateFromFilter: null,
      dateToFilter: null,
      productFilter: [],
      paymentFrequencyFilter: [],
      currencyFilter: [],
      policyNumberFilter: ''
    };
    
    if (this.shouldRestrictToOwnLogs && this.currentUserFullName) {
      this.currentSharedFilter.biztositoFilter = [this.currentUserFullName];
    }
    
    this.pageIndex = 0;
    this.loadInteractions();
  }

  private updateUserOptions() {
    if (!this.allUsers || this.allUsers.length === 0) {
      this.customUserOptions = [];
      return;
    }
    
    if (this.shouldRestrictToOwnLogs && this.currentUserFullName) {
      this.customUserOptions = [{
        value: this.currentUserFullName,
        label: this.currentUserFullName
      }];
    } else {
      const userNames = [...new Set(
        this.allUsers
          .map(user => user.name)
          .filter(name => name)
      )].sort();
      
      this.customUserOptions = userNames.map(user => ({
        value: user,
        label: user
      }));
    }

    this.cdr.detectChanges();
  }
}